import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import "../../../style/icon/tabler-icons/webfont/tabler-icons.css";
import { setExpandMenu } from "../../data/redux/sidebarSlice";
import { selectUser } from "../../data/redux/slice/user";
import {
  resetAllMode,
  setDataLayout,
} from "../../data/redux/themeSettingSlice";
import usePreviousRoute from "./usePreviousRoute";
import { filterSidebarByPermissions } from "../../../utils";
import { canViewActivityLog, isSuperAdmin, isTeacher } from "../../../utils/rbacHelper";
import { getTeacherAssignments } from "../../../service/teacher-portal";

const Sidebar = () => {
  const Location = useLocation();
  const user = useSelector(selectUser);
  const [subOpen, setSubopen] = useState<string[]>([]);
  const [subsidebar, setSubsidebar] = useState<string[]>([]);
  // Defaults true so the FY1 link doesn't flash and disappear while the
  // check is in flight -- filterSidebarByPermissions only restricts it once
  // this resolves to false for a teacher with no FY1 class-teacher duty.
  const [hasFy1Assignment, setHasFy1Assignment] = useState(true);
  const scrollRef = useRef<any>(null);

  useEffect(() => {
    if (!isTeacher(user)) return;
    let cancelled = false;
    getTeacherAssignments()
      .then((r) => {
        if (cancelled) return;
        const items = r?.data?.items || [];
        setHasFy1Assignment(
          items.some((a) => a.is_class_teacher && /foundation year 1/i.test(a.class_name || ""))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  const toggleSidebar = (title: any) => {
    setSubopen((current) => {
      const next = current.includes(title)
        ? current.filter((item) => item !== title)
        : [...current, title];
      localStorage.setItem("menuOpened", JSON.stringify(next));
      return next;
    });
  };

  const toggleSubsidebar = (subitem: any) => {
    setSubsidebar((current) =>
      current.includes(subitem)
        ? current.filter((item) => item !== subitem)
        : [...current, subitem]
    );
  };

  const handleLayoutChange = (layout: string) => {
    dispatch(setDataLayout(layout));
  };

  const handleClick = (label: any, themeSetting: any, layout: any, hasSubmenu: any) => {
    if (hasSubmenu) {
      toggleSidebar(label);
    }
    if (themeSetting) {
      handleLayoutChange(layout);
    }
  };

  const getLayoutClass = (label: any) => {
    switch (label) {
      case "Default":
        return "default_layout";
      case "Mini":
        return "mini_layout";
      case "Box":
        return "boxed_layout";
      case "Dark":
        return "dark_data_theme";
      case "RTL":
        return "rtl";
      default:
        return "";
    }
  };
  const location = useLocation();
  const dispatch = useDispatch();
  const previousLocation = usePreviousRoute();
  const filteredMenus = useMemo(
    () =>
      filterSidebarByPermissions(
        user?.permissions,
        isSuperAdmin(user),
        canViewActivityLog(user),
        isTeacher(user),
        hasFy1Assignment
      ),
    [user, hasFy1Assignment]
  );

  const readStoredMenuOpened = useCallback(() => {
    const storedMenu = localStorage.getItem("menuOpened");
    if (!storedMenu) return [];

    try {
      const parsedMenu = JSON.parse(storedMenu);
      return Array.isArray(parsedMenu) ? parsedMenu : [storedMenu];
    } catch {
      return [storedMenu];
    }
  }, []);

  const itemMatchesPath = useCallback((item: any): boolean => {
    if (!item) return false;

    const paths = [
      item.link,
      item.subLink1,
      item.subLink2,
      item.subLink3,
      item.subLink4,
      item.subLink5,
      item.subLink6,
      item.subLink7,
    ].filter(Boolean);

    return (
      paths.includes(Location.pathname) ||
      item.submenuItems?.some((child: any) => itemMatchesPath(child))
    );
  }, [Location.pathname]);

  useEffect(() => {
    const layoutPages = [
      "/layout-dark",
      "/layout-rtl",
      "/layout-mini",
      "/layout-box",
      "/layout-default",
    ];

    const isCurrentLayoutPage = layoutPages.some((path) =>
      location.pathname.includes(path)
    );
    const isPreviousLayoutPage =
      previousLocation &&
      layoutPages.some((path) => previousLocation.pathname.includes(path));

    if (isPreviousLayoutPage && !isCurrentLayoutPage) {
      dispatch(resetAllMode());
    }
  }, [location, previousLocation, dispatch]);

  useEffect(() => {
    const activeMenuLabels = filteredMenus
      .filter((item: any) => item.submenuItems?.length && itemMatchesPath(item))
      .map((item: any) => item.label);

    setSubopen((current) =>
      Array.from(new Set([...readStoredMenuOpened(), ...current, ...activeMenuLabels]))
    );

    const activeSubmenuLabels = filteredMenus
      .flatMap((item: any) => item.submenuItems || [])
      .filter((item: any) => item.submenuItems?.length && itemMatchesPath(item))
      .map((item: any) => item.label);

    setSubsidebar((current) =>
      Array.from(new Set([...current, ...activeSubmenuLabels]))
    );

    // Select all 'submenu' elements
    const submenus = document.querySelectorAll(".submenu");
    // Loop through each 'submenu'
    submenus.forEach((submenu) => {
      // Find all 'li' elements within the 'submenu'
      const listItems = submenu.querySelectorAll("li");
      submenu.classList.remove("active");
      // Check if any 'li' has the 'active' class
      listItems.forEach((item) => {
        if (item.classList.contains("active")) {
          // Add 'active' class to the 'submenu'
          submenu.classList.add("active");
          return;
        }
      });
    });
  }, [Location.pathname, filteredMenus, itemMatchesPath, readStoredMenuOpened]);

  // Keep the selected menu item visible: on mount and on every route change,
  // scroll the `.active` link into view inside the custom scroll container
  // (which otherwise resets to the top on refresh / returning from a page).
  useEffect(() => {
    const timer = setTimeout(() => {
      const active = document.querySelector(
        "#sidebar-menu a.active"
      ) as HTMLElement | null;
      const view = scrollRef.current?.view as HTMLElement | undefined;
      if (!active || !view) return;
      const viewRect = view.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      // Only scroll if the active item isn't fully visible already.
      if (activeRect.top < viewRect.top || activeRect.bottom > viewRect.bottom) {
        const offset =
          activeRect.top -
          viewRect.top +
          view.scrollTop -
          view.clientHeight / 2 +
          active.clientHeight / 2;
        view.scrollTop = Math.max(0, offset);
      }
    }, 120); // wait for submenu expand + layout
    return () => clearTimeout(timer);
  }, [Location.pathname]);

  const onMouseEnter = () => {
    dispatch(setExpandMenu(true));
  };
  const onMouseLeave = () => {
    dispatch(setExpandMenu(false));
  };

  return (
    <>
      <div
        className="sidebar"
        id="sidebar"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Scrollbars ref={scrollRef}>
          <div className="sidebar-inner slimscroll">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                <li>
                  <Link
                    to="#"
                    // className="d-flex align-items-center border bg-white rounded p-2 mb-4"
                  >
                    {/* <ImageWithBasePath
                      src="assets/img/icons/global-img.svg"
                      className="avatar avatar-md img-fluid rounded"
                      alt="Profile"
                    /> */}
                    {/* <span className="text-dark ms-2 fw-normal">
                      Global International
                    </span> */}
                  </Link>
                </li>
              </ul>

              <ul>
                {filteredMenus?.map((title: any) => (
                  <li
                    className={title?.submenuItems ? "submenu" : ""}
                    key={title.label}
                  >
                    <Link
                      to={title?.submenuItems ? "#" : title?.link}
                      onClick={() =>
                        handleClick(
                          title?.label,
                          title?.themeSetting,
                          getLayoutClass(title?.label),
                          title?.submenuItems
                        )
                      }
                      className={`${
                        subOpen.includes(title?.label) ? "subdrop" : ""
                      } ${itemMatchesPath(title) ? "active" : ""}`}
                    >
                      <i className={title.icon}></i>
                      <span>{title?.label}</span>
                      <span className="badge badge-primary badge-xs text-white fs-10 ms-auto">
                        {title?.version}
                      </span>
                      <span className={title?.submenuItems ? "menu-arrow" : ""} />
                    </Link>
                    {title?.submenuItems && (
                      <ul
                        style={{
                          display: subOpen.includes(title?.label)
                            ? "block"
                            : "none",
                        }}
                      >
                        {title?.submenuItems?.map((item: any) => (
                          <li
                            className={
                              item?.submenuItems ? "submenu submenu-two " : ""
                            }
                            key={item.label}
                          >
                            <Link
                              to={item?.submenuItems ? "#" : item?.link}
                              className={`${
                                itemMatchesPath(item) ? "active" : ""
                              } ${
                                subsidebar.includes(item?.label)
                                  ? "subdrop"
                                  : ""
                              }`}
                              onClick={() => {
                                if (item?.submenuItems) {
                                  toggleSubsidebar(item?.label);
                                }
                              }}
                            >
                              {item?.label}
                              <span
                                className={item?.submenuItems ? "menu-arrow" : ""}
                              />
                            </Link>
                            {item?.submenuItems ? (
                              <ul
                                style={{
                                  display: subsidebar.includes(item?.label)
                                    ? "block"
                                    : "none",
                                }}
                              >
                                {item?.submenuItems?.map((items: any) => (
                                  <li key={items.label}>
                                    <Link
                                      to={items?.link}
                                      className={`submenu-two ${
                                        itemMatchesPath(items) ? "active" : ""
                                      }`}
                                    >
                                      {items?.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <></>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>
    </>
  );
};

export default Sidebar;
